import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { Trash2 } from 'lucide-react';
import type { Location } from '../types/api';

export interface LocationRow {
  id: number;
  name: string;
  address: string;
  isPrimary: boolean;
}

let nextId = 1;

interface LocationsEditorProps {
  searchId: string;
  standalone?: boolean;
  locations?: LocationRow[];
  onLocationsChange?: (locs: LocationRow[]) => void;
}

export function LocationsEditor({ searchId, standalone = true, locations: controlledLocations, onLocationsChange }: LocationsEditorProps) {
  const { t } = useTranslation();
  const [ownLocations, setOwnLocations] = useState<LocationRow[]>([]);
  const [originalLocations, setOriginalLocations] = useState<LocationRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<Set<number>>(new Set());

  const locations = controlledLocations ?? ownLocations;
  const setLocations = onLocationsChange ?? setOwnLocations;

  const loadLocations = useCallback(async () => {
    if (!standalone) return;
    setLoading(true);
    try {
      const locs = await api.getLocations(searchId);
      const rows = locs.map((l: Location) => ({ id: nextId++, name: l.name, address: l.address, isPrimary: l.isPrimary ?? false }));
      if (rows.length > 0 && !rows.some(r => r.isPrimary)) {
        rows[0].isPrimary = true;
      }
      setOwnLocations(rows);
      setOriginalLocations(rows);
    } catch {
      // Locations may not exist yet
    } finally {
      setLoading(false);
    }
  }, [searchId, standalone]);

  useEffect(() => {
    if (standalone) loadLocations();
  }, [loadLocations, standalone]);

  const handleSave = async () => {
    const emptyIds = new Set<number>();
    locations.forEach((l) => {
      if (!l.name.trim() || !l.address.trim()) emptyIds.add(l.id);
    });
    if (emptyIds.size > 0) {
      setValidation(emptyIds);
      return;
    }
    setValidation(new Set());

    setSaving(true);
    setMessage(null);
    try {
      const filled = locations.map(({ name, address, isPrimary }) => ({ name, address, isPrimary }));
      await api.updateLocations(searchId, filled);
      setOriginalLocations(locations);
      setMessage({ text: t('app.locationsEditor.successMessage'), isError: false });
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({ text: t('app.locationsEditor.failedMessage'), isError: true });
    } finally {
      setSaving(false);
    }
  };

  const updateLocation = (index: number, field: 'name' | 'address', value: string) => {
    setLocations(locations.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
    setValidation((prev) => { const next = new Set(prev); next.delete(locations[index]?.id); return next; });
  };

  const setPrimaryLocation = (index: number) => {
    setLocations(locations.map((l, i) => ({ ...l, isPrimary: i === index })));
  };

  const removeLocation = (index: number) => {
    const next = locations.filter((_, i) => i !== index);
    if (next.length > 0 && !next.some(l => l.isPrimary)) {
      next[0] = { ...next[0], isPrimary: true };
    }
    setLocations(next);
  };

  const addLocation = () => {
    setLocations([...locations, { id: nextId++, name: '', address: '', isPrimary: locations.length === 0 }]);
  };

  const locationsChanged = standalone && JSON.stringify(locations) !== JSON.stringify(originalLocations);

  if (loading) {
    return <p className="settings-loading">{t('app.locationsEditor.loading')}</p>;
  }

  return (
    <>
      {locations.length > 0 && (
        <div className="settings-location-labels">
          <span className="settings-location-label-primary">{t('app.locationsEditor.primaryHeader')}</span>
          <span className="settings-location-label-field">{t('app.locationsEditor.labelHeader')}</span>
          <span className="settings-location-label-field">{t('app.locationsEditor.addressHeader')}</span>
          <span className="settings-location-label-action"></span>
        </div>
      )}
      {locations.map((loc, i) => (
        <div key={loc.id} className="settings-location-row">
          <label className="settings-primary-radio" title="Set as primary">
            <input
              type="radio"
              name="primary-location"
              checked={loc.isPrimary}
              onChange={() => setPrimaryLocation(i)}
              disabled={saving}
            />
            <span className="settings-primary-label">{loc.isPrimary ? 'Primary' : ''}</span>
          </label>
          <input
            className={`input ${validation.has(loc.id) && !loc.name.trim() ? 'input-error' : ''}`}
            type="text"
            placeholder={t('app.locationsEditor.namePlaceholder')}
            value={loc.name}
            onChange={(e) => updateLocation(i, 'name', e.target.value)}
            disabled={saving}
          />
          <input
            className={`input ${validation.has(loc.id) && !loc.address.trim() ? 'input-error' : ''}`}
            type="text"
            placeholder={t('app.locationsEditor.addressPlaceholder')}
            value={loc.address}
            onChange={(e) => updateLocation(i, 'address', e.target.value)}
            disabled={saving}
          />
          <button
            className="settings-location-remove"
            onClick={() => removeLocation(i)}
            disabled={saving}
            aria-label={t('app.locationsEditor.removeLabel')}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <button
        className="settings-location-add"
        onClick={addLocation}
        disabled={saving}
      >
        {t('app.locationsEditor.addButton')}
      </button>
      {standalone && locationsChanged && (
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={saving}
          style={{ marginTop: '8px' }}
        >
          {saving ? t('app.locationsEditor.saving') : t('app.locationsEditor.saveButton')}
        </button>
      )}
      {message && (
        <p className={`settings-message ${message.isError ? 'error' : 'success'}`}>
          {message.text}
        </p>
      )}
    </>
  );
}
